import React from 'react';

const HEADING_FONT = 'Manrope, sans-serif';
const BODY_FONT = 'Inter, sans-serif';

/**
 * Call to Action Block Renderer
 * Variants: banner, split, simple
 */
export default function CTARenderer({ props, variant }) {
  const {
    headline = 'Ready to get started?',
    subheadline = '',
    cta_text = 'Get Started',
    cta_url = '#',
    background_color = '#1c1917',
    text_color = '#ffffff',
  } = props;

  const buttonStyle = {
    backgroundColor: text_color,
    color: background_color,
    fontFamily: BODY_FONT,
  };

  // Banner variant: full-width colored banner
  if (variant === 'banner') {
    return (
      <section
        className="py-16 px-6"
        style={{ backgroundColor: background_color }}
      >
        <div className="max-w-4xl mx-auto text-center space-y-4">
          <h2
            className="text-3xl sm:text-4xl font-bold"
            style={{ fontFamily: HEADING_FONT, color: text_color }}
          >
            {headline}
          </h2>
          {subheadline && (
            <p
              className="text-lg opacity-80 max-w-2xl mx-auto"
              style={{ fontFamily: BODY_FONT, color: text_color }}
            >
              {subheadline}
            </p>
          )}
          {cta_text && (
            <div className="pt-2">
              <a
                href={cta_url || '#'}
                className="inline-block px-8 py-3.5 rounded-lg font-semibold text-sm sm:text-base transition-opacity hover:opacity-90"
                style={buttonStyle}
              >
                {cta_text}
              </a>
            </div>
          )}
        </div>
      </section>
    );
  }

  // Split variant: text left, button right
  if (variant === 'split') {
    return (
      <section
        className="py-12 px-6"
        style={{ backgroundColor: background_color }}
      >
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex-1">
            <h2
              className="text-2xl sm:text-3xl font-bold mb-2"
              style={{ fontFamily: HEADING_FONT, color: text_color }}
            >
              {headline}
            </h2>
            {subheadline && (
              <p
                className="text-base opacity-80"
                style={{ fontFamily: BODY_FONT, color: text_color }}
              >
                {subheadline}
              </p>
            )}
          </div>
          {cta_text && (
            <div className="flex-shrink-0">
              <a
                href={cta_url || '#'}
                className="inline-block px-8 py-3.5 rounded-lg font-semibold text-sm sm:text-base transition-opacity hover:opacity-90 whitespace-nowrap"
                style={buttonStyle}
              >
                {cta_text}
              </a>
            </div>
          )}
        </div>
      </section>
    );
  }

  // Simple variant: centered text + button, no background color
  if (variant === 'simple') {
    return (
      <section className="py-16 px-6">
        <div className="max-w-3xl mx-auto text-center space-y-4">
          <h2
            className="text-3xl sm:text-4xl font-bold text-stone-900"
            style={{ fontFamily: HEADING_FONT }}
          >
            {headline}
          </h2>
          {subheadline && (
            <p
              className="text-lg text-stone-500 max-w-xl mx-auto"
              style={{ fontFamily: BODY_FONT }}
            >
              {subheadline}
            </p>
          )}
          {cta_text && (
            <div className="pt-2">
              <a
                href={cta_url || '#'}
                className="inline-block px-8 py-3.5 rounded-lg font-semibold text-sm sm:text-base transition-opacity hover:opacity-90"
                style={{
                  backgroundColor: background_color,
                  color: text_color,
                  fontFamily: BODY_FONT,
                }}
              >
                {cta_text}
              </a>
            </div>
          )}
        </div>
      </section>
    );
  }

  // Fallback to banner
  return (
    <section
      className="py-16 px-6"
      style={{ backgroundColor: background_color }}
    >
      <div className="max-w-4xl mx-auto text-center space-y-4">
        <h2
          className="text-3xl font-bold"
          style={{ fontFamily: HEADING_FONT, color: text_color }}
        >
          {headline}
        </h2>
        {subheadline && (
          <p className="text-lg opacity-80" style={{ fontFamily: BODY_FONT, color: text_color }}>
            {subheadline}
          </p>
        )}
        {cta_text && (
          <a
            href={cta_url || '#'}
            className="inline-block px-8 py-3.5 rounded-lg font-semibold transition-opacity hover:opacity-90"
            style={buttonStyle}
          >
            {cta_text}
          </a>
        )}
      </div>
    </section>
  );
}
